import {
  MODAL_SCHEDULE_CRAWL,
  MODAL_UPGRADE_PLAN,
} from '@components/App/Modal/constants'
import { ProBadge } from '@components/ProBadge'
import type { ScheduleDto } from '@octopus-do/octopus-api-client'
import { Button } from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { PLAN_FREE } from '@/constants/userSubscriptionPlans'
import css from './styles.module.css'

interface Props {
  onScheduled: () => void
  schedule: ScheduleDto | null
}

export const ScheduleButton: React.FC<Props> = ({ onScheduled, schedule }) => {
  const dispatch = useDispatch()
  const ownerPlan = useSelector(migrationProjectSelector.getProjectOwnerPlan)

  // Scheduling is a paid feature. On a free plan the button is dead and wears
  // the Pro badge that says why, rather than opening a dialog that could not
  // save anything.
  const freeOwner = ownerPlan === PLAN_FREE
  const isScheduled = schedule !== null && schedule.schedule !== 'off'

  const handleOnClick = () => {
    if (freeOwner) {
      sendGtmEvent(GtmTriggerName.REPORT_SCHEDULE_UPGRADE)
      dispatch(
        openModal({
          modalType: MODAL_UPGRADE_PLAN,
          modalProps: { planType: 'pro' },
        })
      )
      return
    }

    // The screen already read the schedule with the rest of the overview, so
    // the dialog is handed it and opens on the real setting instead of on a
    // spinner.
    dispatch(
      openModal({
        modalType: MODAL_SCHEDULE_CRAWL,
        modalProps: { initial: schedule, onSaved: onScheduled },
      })
    )
  }

  return (
    // The dot has to sit outside the button: the button clips its own
    // overflow, and the dot overhangs the corner the way the header's Play
    // button wears it.
    <span className={css.wrapper}>
      {freeOwner ? (
        <Button
          className={css.disabled}
          priority="secondary"
          size="medium"
          label="Schedule"
          right={ProBadge}
          onClick={handleOnClick}
        />
      ) : (
        <Button
          priority="secondary"
          size="medium"
          label="Schedule"
          onClick={handleOnClick}
        />
      )}
      {!freeOwner && isScheduled && <span className={css.dot} />}
    </span>
  )
}
